import {
  HangerList,
  LumberGrade,
  LumberListPrototype,
  LumberSet,
  LumberSpecies,
  OrganizationMaterialSet,
  OrganizationMaterialSetEditResponse,
  OrganizationMaterialSetPrototype,
  PlateList,
  PlateOption,
} from '@paragon/contract-library';

import { DesignService } from '../connections';

const e = encodeURIComponent;

function plateListUrl(organization: string, plateListGuid: string) {
  return `materialSets/${e(organization)}/plateLists/${e(plateListGuid)}`;
}

function hangerListUrl(organization: string, hangerListGuid: string) {
  return `materialSets/${e(organization)}/hangerLists/${e(hangerListGuid)}`;
}

function lumberListUrl(organization: string, lumberListGuid: string) {
  return `materialSets/${e(organization)}/lumberLists/${e(lumberListGuid)}`;
}

function lumberSetUrl(organization: string, lumberListGuid: string, lumberSetGuid: string) {
  return `${lumberListUrl(organization, lumberListGuid)}/lumberSets/${e(lumberSetGuid)}`;
}

export async function getOrganizationMaterialSet(organization: string): Promise<OrganizationMaterialSet> {
  return await DesignService.get(`materialSets/${e(organization)}`, {
    urlEncode: false,
  });
}

export async function getOrganizationMaterialSetPrototype(
  organization: string,
): Promise<OrganizationMaterialSetPrototype> {
  return await DesignService.get(`materialSets/${e(organization)}/prototype`, { urlEncode: false });
}

export function setDefaultLumberListGuid(organization: string, lumberListGuid: string): Promise<void> {
  return DesignService.put(`materialSets/${e(organization)}/defaultMaterialSelection/lumberListGuid`, {
    urlEncode: false,
    data: lumberListGuid,
  });
}

export function setDefaultPlateListGuid(organization: string, plateListGuid: string): Promise<void> {
  return DesignService.put(`materialSets/${e(organization)}/defaultMaterialSelection/plateListGuid`, {
    urlEncode: false,
    data: plateListGuid,
  });
}

export function setDefaultHangerListGuid(organization: string, hangerListGuid: string): Promise<void> {
  return DesignService.put(`materialSets/${e(organization)}/defaultMaterialSelection/hangerListGuid`, {
    urlEncode: false,
    data: hangerListGuid,
  });
}

export async function renameHangerList(
  organization: string,
  hangerListGuid: string,
  name: string,
): Promise<OrganizationMaterialSetEditResponse> {
  return await DesignService.patch(`${hangerListUrl(organization, hangerListGuid)}/name`, {
    data: name,
    urlEncode: false,
  });
}

export async function archiveHangerList(
  organization: string,
  hangerListGuid: string,
): Promise<OrganizationMaterialSetEditResponse> {
  return await DesignService.delete(hangerListUrl(organization, hangerListGuid), {
    urlEncode: false,
  });
}

export async function restoreHangerList(
  organization: string,
  hangerListGuid: string,
): Promise<OrganizationMaterialSetEditResponse> {
  return await DesignService.post(`${hangerListUrl(organization, hangerListGuid)}/restore`, {
    urlEncode: false,
  });
}

export async function upsertHangerList(
  organization: string,
  hangerList: HangerList,
): Promise<OrganizationMaterialSetEditResponse> {
  return await DesignService.put(hangerListUrl(organization, hangerList.guid), {
    urlEncode: false,
    data: hangerList,
  });
}

export async function upsertPlateList(
  organization: string,
  plateList: PlateList,
): Promise<OrganizationMaterialSetEditResponse> {
  return await DesignService.put(plateListUrl(organization, plateList.guid), {
    urlEncode: false,
    data: plateList,
  });
}

export async function renamePlateList(
  organization: string,
  plateListGuid: string,
  name: string,
): Promise<OrganizationMaterialSetEditResponse> {
  return await DesignService.patch(`${plateListUrl(organization, plateListGuid)}/name`, {
    data: name,
    urlEncode: false,
  });
}

export async function archivePlateList(
  organization: string,
  plateListGuid: string,
): Promise<OrganizationMaterialSetEditResponse> {
  return await DesignService.delete(plateListUrl(organization, plateListGuid), {
    urlEncode: false,
  });
}

export async function restorePlateList(
  organization: string,
  plateListGuid: string,
): Promise<OrganizationMaterialSetEditResponse> {
  return await DesignService.post(`${plateListUrl(organization, plateListGuid)}/restore`, {
    urlEncode: false,
  });
}

export async function upsertLumberList(
  organization: string,
  lumberListGuid: string,
  prototype: LumberListPrototype,
): Promise<OrganizationMaterialSetEditResponse> {
  return await DesignService.put(lumberListUrl(organization, lumberListGuid), {
    urlEncode: false,
    data: prototype,
  });
}

export async function renameLumberList(
  organization: string,
  lumberListGuid: string,
  name: string,
): Promise<OrganizationMaterialSetEditResponse> {
  return await DesignService.patch(`${lumberListUrl(organization, lumberListGuid)}/name`, {
    data: name,
    urlEncode: false,
  });
}

export async function archiveLumberList(
  organization: string,
  lumberListGuid: string,
): Promise<OrganizationMaterialSetEditResponse> {
  return await DesignService.delete(lumberListUrl(organization, lumberListGuid), { urlEncode: false });
}

export async function restoreLumberList(
  organization: string,
  lumberListGuid: string,
): Promise<OrganizationMaterialSetEditResponse> {
  return await DesignService.post(`${lumberListUrl(organization, lumberListGuid)}/restore`, {
    urlEncode: false,
  });
}

export async function upsertLumberSet(
  organization: string,
  lumberListGuid: string,
  lumberSetGuid: string,
  lumberSet: LumberSet,
): Promise<OrganizationMaterialSetEditResponse> {
  return await DesignService.put(lumberSetUrl(organization, lumberListGuid, lumberSetGuid), {
    data: lumberSet,
    urlEncode: false,
  });
}

export async function deleteLumberSet(
  organization: string,
  lumberListGuid: string,
  lumberSetGuid: string,
): Promise<OrganizationMaterialSetEditResponse> {
  return await DesignService.delete(lumberSetUrl(organization, lumberListGuid, lumberSetGuid), { urlEncode: false });
}

export function getPlateOptions(): Promise<PlateOption[]> {
  return DesignService.get(`materialSets/platePairs/all`);
}

/**
 * A group of species and grades that are allowed to be used together.
 */
type LumberOptionGroup = {
  readonly species: readonly LumberSpecies[];
  readonly grades: readonly LumberGrade[];
};
export type LumberOptionGroups = readonly LumberOptionGroup[];

export function getLumberSpeciesAndGradeOptions(): Promise<LumberOptionGroups> {
  return DesignService.get(`materialSets/lumber/speciesAndGradeGroups`);
}
